import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  ConciergeBell, X, Send, Loader2, AlertCircle, ArrowUpRight, ShieldCheck,
} from "lucide-react";

const SESSION_KEY = "lch_concierge_session";
const SUGGESTIONS = [
  "What Rolex models do you have available?",
  "Show me dress watches under $15k",
  "I'd like to sell my watch",
  "How does authentication work?",
];

/* ---------------- rich text rendering (bold + internal listing links) ---------------- */
function renderInline(text, keyPrefix) {
  const nodes = [];
  const regex = /(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  let idx = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(<React.Fragment key={`${keyPrefix}-t${idx}`}>{text.slice(last, m.index)}</React.Fragment>);
    const token = m[0];
    if (token.startsWith("**")) {
      nodes.push(<strong key={`${keyPrefix}-b${idx}`} className="font-semibold text-[var(--text)]">{token.slice(2, -2)}</strong>);
    } else {
      const lm = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const label = lm[1];
      const url = lm[2];
      if (url.startsWith("/")) {
        nodes.push(
          <Link key={`${keyPrefix}-l${idx}`} to={url} data-testid="concierge-inline-link"
            className="text-[#c9a86a] underline decoration-[#c9a86a]/40 underline-offset-2 hover:decoration-[#c9a86a]">
            {label}
          </Link>
        );
      } else {
        nodes.push(<span key={`${keyPrefix}-l${idx}`} className="text-[#c9a86a]">{label}</span>);
      }
    }
    last = m.index + token.length;
    idx += 1;
  }
  if (last < text.length) nodes.push(<React.Fragment key={`${keyPrefix}-t${idx}`}>{text.slice(last)}</React.Fragment>);
  return nodes;
}

function RichText({ text }) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim() !== "" || true);
  const out = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith("- ") || lines[i].startsWith("* "))) {
        items.push(lines[i].slice(2)); i++;
      }
      out.push(
        <ul key={`ul${key++}`} className="my-1.5 space-y-1">
          {items.map((it, j) => (
            <li key={j} className="flex gap-2 leading-relaxed">
              <span className="text-[#c9a86a] mt-0.5">—</span>
              <span>{renderInline(it, `li${key}-${j}`)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }
    out.push(<p key={`p${key++}`} className="leading-relaxed my-1.5 first:mt-0 last:mb-0">{renderInline(line, `p${key}`)}</p>);
    i++;
  }
  return <div className="text-sm text-[var(--text-2)]">{out}</div>;
}

/* ---------------- grounded listing card ---------------- */
function MatchCard({ w }) {
  return (
    <Link
      to={w.link}
      data-testid="concierge-match-card"
      className="group flex items-center gap-3 rounded-lg border border-[var(--border-lch)] bg-[var(--panel-2)] p-2.5 transition-transform duration-200 hover:-translate-y-0.5 hover:border-[#c9a86a]/50"
    >
      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-[var(--bg-2)] border border-[var(--border-lch)]">
        {w.image ? (
          <img src={w.image} alt={`${w.brand} ${w.model}`} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[var(--muted)]">
            <ConciergeBell size={16} />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-[var(--text)]">
          {w.brand} {w.model}
        </div>
        <div className="truncate text-[11px] text-[var(--muted)]">
          {w.reference_number ? `Ref ${w.reference_number} · ` : ""}{w.price_display}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-[var(--text-2)]">{w.status}</span>
        </div>
      </div>
      <ArrowUpRight size={14} className="shrink-0 text-[var(--muted)] transition-colors group-hover:text-[#c9a86a]" />
    </Link>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1" data-testid="concierge-typing">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-1.5 w-1.5 rounded-full bg-[#c9a86a]/70 animate-pulse"
          style={{ animationDelay: `${i * 160}ms` }} />
      ))}
    </div>
  );
}

/* ---------------- main widget ---------------- */
export default function ConciergeWidget() {
  const { customer } = useCustomerAuth();
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content, matches?, lead_saved?, high_intent?}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const lastFailedRef = useRef("");

  // capability check
  useEffect(() => {
    let alive = true;
    publicApi.get("/chat/config")
      .then((r) => { if (alive) setAvailable(!!r.data?.configured); })
      .catch(() => { if (alive) setAvailable(true); });
    return () => { alive = false; };
  }, []);

  // session id
  useEffect(() => {
    let sid = null;
    try { sid = window.localStorage.getItem(SESSION_KEY); } catch (e) { /* ignore */ }
    if (!sid) {
      sid = `s-${(crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36))}`.replace(/[^A-Za-z0-9_-]/g, "");
      try { window.localStorage.setItem(SESSION_KEY, sid); } catch (e) { /* ignore */ }
    }
    setSessionId(sid);
  }, []);

  // load history when panel first opens
  const [historyLoaded, setHistoryLoaded] = useState(false);
  useEffect(() => {
    if (!open || historyLoaded || !sessionId) return;
    setHistoryLoaded(true);
    publicApi.get("/chat/history", { params: { session_id: sessionId } })
      .then((r) => {
        const msgs = (r.data?.messages || []).map((m) => ({ role: m.role, content: m.content }));
        if (msgs.length) setMessages(msgs);
      })
      .catch(() => { /* start fresh */ });
  }, [open, historyLoaded, sessionId]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  useEffect(() => { if (open) scrollToBottom(); }, [messages, loading, open, scrollToBottom]);

  // esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setError("");
    lastFailedRef.current = "";
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const r = await publicApi.post("/chat", { message: msg, session_id: sessionId });
      const data = r.data || {};
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try { window.localStorage.setItem(SESSION_KEY, data.session_id); } catch (e) { /* ignore */ }
      }
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: data.reply || "",
        matches: data.matches || [],
        lead_saved: data.lead_saved,
        high_intent: data.high_intent,
      }]);
    } catch (e) {
      const status = e?.response?.status;
      lastFailedRef.current = msg;
      setError(status === 429
        ? "You're sending messages a little quickly — please pause a moment."
        : "The concierge is briefly unavailable. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId]);

  const retry = useCallback(() => {
    const msg = lastFailedRef.current;
    if (!msg) return;
    // remove the trailing unanswered user bubble, then resend
    setMessages((prev) => {
      const copy = [...prev];
      if (copy.length && copy[copy.length - 1].role === "user") copy.pop();
      return copy;
    });
    send(msg);
  }, [send]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!available) return null;

  const greetingName = customer?.name ? `, ${customer.name.split(" ")[0]}` : "";

  return (
    <div className="lch-public">
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="concierge-open-btn"
          aria-label="Open the concierge"
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2.5 rounded-full border border-[#c9a86a]/40 bg-[var(--panel)] px-4 py-3 text-[var(--text)] shadow-[0_18px_50px_rgba(0,0,0,0.35)] transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a86a]/60"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#c9a86a] text-[var(--on-gold)]">
            <ConciergeBell size={18} />
          </span>
          <span className="hidden pr-1 text-sm font-medium tracking-wide sm:inline">Concierge</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="Watch concierge"
          data-testid="concierge-panel"
          className="fixed inset-x-0 bottom-0 z-40 flex h-[100dvh] w-full flex-col border border-[var(--border-lch)] bg-[var(--panel)] text-[var(--text)] shadow-[0_24px_70px_rgba(0,0,0,0.5)] sm:inset-auto sm:bottom-6 sm:right-6 sm:h-[600px] sm:w-[390px] sm:rounded-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-[var(--border-lch)] px-4 py-3.5 sm:rounded-t-2xl">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#c9a86a] text-[var(--on-gold)]">
                <ConciergeBell size={18} />
              </span>
              <div className="leading-tight">
                <div className="text-sm font-semibold tracking-wide" style={{ fontFamily: "var(--font-serif, serif)" }}>
                  The Concierge
                </div>
                <div className="text-[11px] text-[var(--muted)]">Le Collectif Horloger</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="concierge-close-btn"
              aria-label="Close concierge"
              className="text-[var(--muted)] transition-colors hover:text-[var(--text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a86a]/60 rounded-md p-1"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4" data-testid="concierge-messages">
            {messages.length === 0 && (
              <div className="space-y-4" data-testid="concierge-empty-state">
                <div className="rounded-xl border border-[var(--border-lch)] bg-[var(--panel-2)] p-4">
                  <p className="text-sm text-[var(--text-2)]">
                    Welcome{greetingName}. I can help you browse the collection, answer horology
                    questions, or arrange a private request. How may I assist?
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="text-[11px] uppercase tracking-wider text-[var(--muted)]">Try asking</div>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => send(s)}
                        data-testid="concierge-suggestion"
                        className="rounded-full border border-[var(--border-lch)] bg-[var(--panel)] px-3 py-1.5 text-xs text-[var(--text-2)] transition-colors hover:border-[#c9a86a]/50 hover:text-[var(--text)]"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {messages.map((m, i) => (
                <div key={i} data-testid={`concierge-msg-${m.role}`}>
                  {m.role === "user" ? (
                    <div className="flex justify-end">
                      <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#c9a86a] px-3.5 py-2 text-sm text-[var(--on-gold)]">
                        {m.content}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="max-w-[92%] rounded-2xl rounded-bl-sm border border-[var(--border-lch)] bg-[var(--panel-2)] px-3.5 py-2.5">
                        <RichText text={m.content} />
                      </div>
                      {Array.isArray(m.matches) && m.matches.length > 0 && (
                        <div className="space-y-2" data-testid="concierge-matches">
                          {m.matches.slice(0, 4).map((w) => <MatchCard key={w.id} w={w} />)}
                        </div>
                      )}
                      {m.lead_saved && (
                        <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-2)]" data-testid="concierge-lead-saved">
                          <ShieldCheck size={13} className="text-[#c9a86a]" />
                          {m.high_intent ? "Shared with our specialists — priority follow-up." : "Your details were shared with our team."}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex flex-col gap-2" data-testid="concierge-loading">
                  <div className="max-w-[92%] rounded-2xl rounded-bl-sm border border-[var(--border-lch)] bg-[var(--panel-2)] px-3.5 py-2.5">
                    <TypingDots />
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-[var(--lch-danger,#8a2f2f)]/40 bg-[var(--lch-danger,#8a2f2f)]/10 px-3 py-2" data-testid="concierge-error">
                  <div className="flex items-center gap-2 text-xs text-[var(--text-2)]">
                    <AlertCircle size={14} className="shrink-0 text-[var(--lch-danger,#8a2f2f)]" />
                    <span>{error}</span>
                  </div>
                  {lastFailedRef.current && (
                    <button type="button" onClick={retry} data-testid="concierge-retry-btn"
                      className="shrink-0 text-xs font-medium text-[#c9a86a] hover:underline">
                      Retry
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Composer */}
          <div className="border-t border-[var(--border-lch)] p-3 sm:rounded-b-2xl">
            <div className="flex items-end gap-2">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                maxLength={2000}
                placeholder="Ask about a watch, a request, or a sale…"
                data-testid="concierge-input"
                className="max-h-28 min-h-[42px] resize-none border-[var(--border-lch)] bg-[var(--panel-2)] text-sm text-[var(--text)] placeholder:text-[var(--muted)] focus-visible:ring-1 focus-visible:ring-[#c9a86a]/60"
              />
              <Button
                type="button"
                onClick={() => send()}
                disabled={loading || !input.trim()}
                data-testid="concierge-send-btn"
                aria-label="Send message"
                className="h-[42px] w-[42px] shrink-0 rounded-xl bg-[#c9a86a] p-0 text-[var(--on-gold)] hover:bg-[#b89455] disabled:opacity-50"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </Button>
            </div>
            <p className="mt-2 px-1 text-[10px] leading-relaxed text-[var(--muted)]">
              Details are shared only with your consent. Availability &amp; authentication are confirmed by our team.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
